import React, { useEffect, useState, useRef } from 'react';
import {
  Table, Thead, Tbody, Tr, Th, Td, Button, Badge,
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  useDisclosure, Flex, Text, Box, HStack, Select, Input, Tag,
  AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
  AlertDialogContent, AlertDialogOverlay, useToast,
} from '@chakra-ui/react';
import { AddIcon, DownloadIcon } from '@chakra-ui/icons';
import * as XLSX from 'xlsx';
import { ImmBanavim, listarBanavim, eliminarBanavim } from '../../../api/immApi';
import { fmt, IMM_COLOR, IMM_COLOR_HOVER, EstatusBadge } from '../_shared';
import FormularioBanavim from './FormularioBanavim';

const expedienteLabel = (item: ImmBanavim) => item.expediente?.folio ?? `#${item.expedienteId}`;

const TablaBanavim: React.FC = () => {
  const toast = useToast();
  const cancelRef = useRef<any>(null);

  const verD = useDisclosure();
  const editarD = useDisclosure();
  const nuevoD = useDisclosure();
  const bajaD = useDisclosure();

  const [data, setData] = useState<ImmBanavim[]>([]);
  const [selected, setSelected] = useState<ImmBanavim | null>(null);
  const [cargando, setCargando] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstatus, setFiltroEstatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const fetchData = async () => {
    setCargando(true);
    try {
      setData(await listarBanavim());
    } catch {
      setData([]);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const estatuses = Array.from(new Set(data.map((i) => i.estatus).filter(Boolean)));

  const datosFiltrados = data.filter((item) => {
    const matchEstatus = filtroEstatus ? item.estatus === filtroEstatus : true;
    const q = busqueda.toLowerCase();
    const matchBusqueda = q
      ? expedienteLabel(item).toLowerCase().includes(q) ||
        item.folioBanavim?.toLowerCase().includes(q)
      : true;
    return matchEstatus && matchBusqueda;
  });

  const totalPages = Math.ceil(datosFiltrados.length / itemsPerPage);
  const indexUltimo = currentPage * itemsPerPage;
  const indexPrimero = indexUltimo - itemsPerPage;
  const itemsPagina = datosFiltrados.slice(indexPrimero, indexUltimo);

  const handleGuardado = () => { nuevoD.onClose(); editarD.onClose(); fetchData(); };

  const handleBajaConfirmar = async () => {
    if (!selected) return;
    setProcesando(true);
    try {
      await eliminarBanavim(selected.id);
      toast({ title: 'Registro eliminado', status: 'success', duration: 3000 });
      bajaD.onClose();
      fetchData();
    } catch (error: any) {
      toast({ title: 'Error', description: error?.response?.data?.message || 'No se pudo eliminar.', status: 'error', duration: 4000 });
    } finally {
      setProcesando(false);
    }
  };

  const handleExportExcel = () => {
    const filas = datosFiltrados.map((item) => ({
      'Expediente': expedienteLabel(item),
      'Folio BANAVIM': item.folioBanavim || '',
      'Fecha registro': fmt(item.fechaRegistro),
      'Canalización': item.canalizacionRealizada ? 'Sí' : 'No',
      'Acompañamiento': item.acompanamientoRealizado ? 'Sí' : 'No',
      'Estatus': item.estatus,
    }));
    const ws = XLSX.utils.json_to_sheet(filas);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'BANAVIM IMM');
    XLSX.writeFile(wb, `BANAVIM_IMM_${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  return (
    <Box>
      <Flex mb={4} justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <HStack spacing={3} flexWrap="wrap">
          <Input placeholder="Buscar por expediente o folio BANAVIM..." value={busqueda}
            onChange={(e) => { setBusqueda(e.target.value); setCurrentPage(1); }} size="sm" w={{ base: 'full', md: '280px' }} />
          <Select placeholder="Estatus" value={filtroEstatus} onChange={(e) => { setFiltroEstatus(e.target.value); setCurrentPage(1); }} size="sm" w="160px">
            {estatuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" leftIcon={<DownloadIcon />} colorScheme="green" variant="outline" onClick={handleExportExcel}>Excel</Button>
          <Button size="sm" leftIcon={<AddIcon />} bg={IMM_COLOR} color="white" _hover={{ bg: IMM_COLOR_HOVER }} onClick={nuevoD.onOpen}>Nuevo registro</Button>
        </HStack>
      </Flex>

      <Box overflowX="auto" borderRadius="md" boxShadow="md" bg="white">
        <Table variant="simple" size="sm">
          <Thead bg={IMM_COLOR}>
            <Tr>
              <Th color="white" fontSize="xs">Expediente</Th>
              <Th color="white" fontSize="xs">Folio BANAVIM</Th>
              <Th color="white" fontSize="xs">Fecha registro</Th>
              <Th color="white" fontSize="xs">Canalización</Th>
              <Th color="white" fontSize="xs">Acompañamiento</Th>
              <Th color="white" fontSize="xs">Estatus</Th>
              <Th color="white" fontSize="xs">Acciones</Th>
            </Tr>
          </Thead>
          <Tbody>
            {cargando ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.500">Cargando...</Text></Td></Tr>
            ) : itemsPagina.length === 0 ? (
              <Tr><Td colSpan={7} textAlign="center" py={8}><Text color="gray.400">No se encontraron registros</Text></Td></Tr>
            ) : (
              itemsPagina.map((item, idx) => (
                <Tr key={item.id} bg={idx % 2 === 0 ? 'white' : 'gray.50'} _hover={{ bg: 'red.50' }}>
                  <Td><Tag size="sm" colorScheme="red" variant="subtle">{expedienteLabel(item)}</Tag></Td>
                  <Td>{item.folioBanavim || '—'}</Td>
                  <Td>{fmt(item.fechaRegistro)}</Td>
                  <Td><Badge colorScheme={item.canalizacionRealizada ? 'green' : 'gray'}>{item.canalizacionRealizada ? 'Sí' : 'No'}</Badge></Td>
                  <Td><Badge colorScheme={item.acompanamientoRealizado ? 'green' : 'gray'}>{item.acompanamientoRealizado ? 'Sí' : 'No'}</Badge></Td>
                  <Td><EstatusBadge value={item.estatus} /></Td>
                  <Td>
                    <HStack spacing={1}>
                      <Button size="xs" colorScheme="blue" variant="outline" onClick={() => { setSelected(item); verD.onOpen(); }}>Ver</Button>
                      <Button size="xs" colorScheme="orange" variant="outline" onClick={() => { setSelected(item); editarD.onOpen(); }}>Editar</Button>
                      <Button size="xs" colorScheme="red" variant="outline" onClick={() => { setSelected(item); bajaD.onOpen(); }}>Eliminar</Button>
                    </HStack>
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {totalPages > 1 && (
        <Flex mt={4} justify="space-between" align="center">
          <Text fontSize="sm" color="gray.500">Mostrando {indexPrimero + 1}–{Math.min(indexUltimo, datosFiltrados.length)} de {datosFiltrados.length}</Text>
          <HStack spacing={1}>
            <Button size="xs" onClick={() => setCurrentPage(1)} isDisabled={currentPage === 1}>«</Button>
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} isDisabled={currentPage === 1}>‹</Button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              const page = Math.max(1, currentPage - 2) + i;
              if (page > totalPages) return null;
              return (
                <Button key={page} size="xs" bg={currentPage === page ? IMM_COLOR : undefined} color={currentPage === page ? 'white' : undefined} onClick={() => setCurrentPage(page)}>{page}</Button>
              );
            })}
            <Button size="xs" onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} isDisabled={currentPage === totalPages}>›</Button>
            <Button size="xs" onClick={() => setCurrentPage(totalPages)} isDisabled={currentPage === totalPages}>»</Button>
          </HStack>
        </Flex>
      )}

      {/* Ver */}
      <Modal isOpen={verD.isOpen} onClose={verD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Registro BANAVIM</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioBanavim modo="view" data={selected} />}</ModalBody>
          <ModalFooter><Button onClick={verD.onClose}>Cerrar</Button></ModalFooter>
        </ModalContent>
      </Modal>

      {/* Editar */}
      <Modal isOpen={editarD.isOpen} onClose={editarD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Editar registro BANAVIM</ModalHeader>
          <ModalBody py={5}>{selected && <FormularioBanavim modo="edit" data={selected} onGuardado={handleGuardado} onCancelar={editarD.onClose} />}</ModalBody>
        </ModalContent>
      </Modal>

      {/* Nuevo */}
      <Modal isOpen={nuevoD.isOpen} onClose={nuevoD.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader borderBottom="1px solid" borderColor="gray.200" color={IMM_COLOR}>Nuevo registro BANAVIM</ModalHeader>
          <ModalBody py={5}><FormularioBanavim modo="new" onGuardado={handleGuardado} onCancelar={nuevoD.onClose} /></ModalBody>
        </ModalContent>
      </Modal>

      {/* Eliminar físico */}
      <AlertDialog isOpen={bajaD.isOpen} leastDestructiveRef={cancelRef} onClose={bajaD.onClose}>
        <AlertDialogOverlay><AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold" color={IMM_COLOR}>Eliminar registro BANAVIM</AlertDialogHeader>
          <AlertDialogBody>
            El registro del expediente <strong>{selected ? expedienteLabel(selected) : ''}</strong> se eliminará de forma permanente. ¿Continuar?
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button ref={cancelRef} onClick={bajaD.onClose} isDisabled={procesando}>Cancelar</Button>
            <Button colorScheme="red" onClick={handleBajaConfirmar} ml={3} isLoading={procesando} loadingText="Procesando...">Eliminar</Button>
          </AlertDialogFooter>
        </AlertDialogContent></AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default TablaBanavim;
